import { ModalProps } from 'components/modal/modal';
import { ReactElement } from 'react';
import { StorageKeys } from 'utils/storageKeys';

export const enum PlatformType {
    MOB = 'mobweb',
    WEB = 'web',
    APP_ANDROID = 'android',
    APP_IOS = 'ios',
    WEB_ANDROID = 'androidweb',
    WEB_IOS = 'iosweb'
}

export enum ApiResultStatus {
    OK = 'ok',
    ERROR = 'error',
    CANCEL = 'cancel',
    EVENT = 'event'
}

export type StatReportTitles = 'payments' | 'paymentErrors' | 'companyTransfers' | 'companyTransferErrors' | 'refunds';

export type ApiResultData = Record<string, any> | string | boolean;

export type ApiCallback = (status: ApiResultStatus, data: ApiResultData) => void;

export type InvokeMethodFunction = (
    methodName: string,
    params?: (string | number | object | boolean | undefined)[],
    callback?: ApiCallback
) => void;

export type UserCallback = (status: string, success: any, error: { error_msg: string } | null) => void;

export interface InitArguments extends Record<string, any> {
    app_id: string | number;
    app_key: string;
    oauth: {
        scope: string;
        url: string;
        state: string;
        layout: string;
    };
    use_extlinks?: {
        customClass: string;
        decorator: string;
    };
    location_search?: string;
    location_hash?: string;
    api_server?: string;
    web_server?: string;
    group_id?: string;
    widget_server?: string;
    mob_server?: string;
    oauth_scope?: string;
    oauth_url?: string;
    oauth_state?: string;
    first_start: string;
    authorized?: string;
    user_name?: string;
    user_stat_id?: string;
    saved_state?: string;
    custom_args?: string;
}

export interface AppParams extends Record<string, any> {
    api_server: string;
    apiconnection: string;
    application_key: string;
    auth_sig: string;
    authorized: string;
    clientLog: string;
    container: string;
    first_start: string;
    ip_geo_location: string;
    lang: string;
    logged_user_id: string;
    new_sig: string;
    session_key: string;
    session_secret_key: string;
    sig: string;
    theme: string;
    web_server: string;
}

export interface IQuestOverridesResponse {
    'missions.mapping.conf': string;
    'winners.conf': string;
}

export interface ISnake {
    id: number;
    name: string;
    custom_params: {
        title: string;
        img: string;
        buyCost: number;
        bg?: string;
        shadow?: string;
    };
}

export interface ICollectionConfig {
    id: number;
    name: string;
    snakes: number[];
    ticket: number;
    rolled: boolean;
    exchange: boolean;
    lotteryId: LotteryIds;
    custom_params: {
        title?: string;
        img?: string;
        bg?: string;
        shadow?: string;
        fullImage?: string;
    };
}

export interface ILootbox {
    id: number;
    type: string;
    day: number;
    prev_year_bonus: string;
    custom_params: Record<string, string>;
}

export enum TicketTypes {
    PRIZE = 'prize'
}

export interface IReward {
    id: number;
    limit: number;
    priority: number;
    type: RewardTypes;
    custom_params: Record<string, string>;
    user_data: Record<string, string>;
    lottery_reward_type: LotteryRewardTypes;
}

export interface IMission {
    id: number;
    type: string;
    reward: number;
    custom_params: Record<string, string>;
}

export interface IConfig {
    server_time: number;
    snakes: ISnake[];
    collections: ICollectionConfig[];
    lootboxes: ILootbox[];
    rewards: IReward[];
    missions: IMission[];
}

export enum LootboxTypes {
    DRAGON = 'dragon'
}

export interface IUnopenedLootbox {
    id: number;
    key: LootboxTypes;
}

export interface ICollectionState {
    id: number;
    snakes: number[];
    ticket: number;
    rolled: boolean;
    exchange: boolean;
}

export enum LotteryRewardTypes {
    LOGINS_SPECIAL = 'LOGINS_SPECIAL',
    RUBIES_SPECIAL = 'RUBIES_SPECIAL',

    CODE = 'CODE',
    FRAME = 'FRAME',
    SURPRIZE = 'SURPRIZE',
    GIFT = 'GIFT',
    OKS = 'OKS',
    RUBY_100 = 'RUBY_100',
    RUBY_50 = 'RUBY_50',
    TOY = 'TOY'
}

export interface IRewardState {
    date: number;
    id: string;
    mission_id: string;
    collection_id: number;
    rolled: boolean;
    lottery_reward_type: LotteryRewardTypes;
    renderParams: {
        name: string;
        img: string;
        congrats: string;
        desc: string;
        background: string;
        modalBackground?: string;
        modalTitle?: string;
        modalDescription?: string;
        modalImg?: string;
        activateLink?: string;
        activateText?: string;
        providerLink?: string;
        providerText?: string;
        logRender?: boolean;
        notificationKey?: string;
    };
    promo_code?: string;
}

export interface ITicket {
    ticket_id: string;
    ticket_uuid: string;
}

export interface IUserState {
    server_time: number;
    day: number;
    opened_lootboxes: number;
    visited_days?: number[];
    collections?: ICollectionState[];
    finished_collection?: number;
    first_visit_for_day: boolean;
    unopened_bonus_lootboxes?: IUnopenedLootbox[];
    current_lutbox_snakes?: number[];
    free_snakes?: number[];
    tickets?: ITicket[];
    lootbox_open_days?: number[];
    exchanged_snake_pairs?: IExchangedSnakePairs[];
}

export enum ToastTypes {
    SUCCESS = 'success',
    ERROR = 'error'
}

export interface IToastConfig {
    text: string | ReactElement;
    type: ToastTypes;
}

export enum QuestTypes {
    LOGINS = 'external_logins', // Зайди в игру N дней. в customParams указать goal с числом, т.к. детей создавать впадлу
    LINK = 'external_link', // Переийти по ссылке (открыть в новой вкладке). Засчитать сразу же при переходе;
    INVITE = 'external_invite', // Пригласи N друзей. Считается через ребенка, ибо иначе никак
    INTERNAL = 'internal' // Любое internal задание, требующее перехода по ссылке
}

export enum RewardTypes {
    PROMO = 'PROMO',
    STICKER = 'STICKER',
    PRESENT = 'PRESENT',
    FRAME = 'FRAME',
    GIFT = 'GIFT',
    TICKET = 'TICKET'
}

export enum QuestRewardTypes {
    SNAKE_2025 = 'snake_2025', // игрушка
    SNAKE_2025_LOOTBOX = 'snake_2025_lootbox', // лутбокс
    SNAKE_2025_TICKET = 'snake_2025_ticket', // билет
    SNAKE_2025_GIFT = 'snake_2025_revolver', // подарок через барабан
    SNAKE_2025_PROMO = 'snake_2025_promo' // промокод
}

export enum QuestConfigType {
    external = 'external'
}

export interface ITaskConfig {
    capacity: number;
    children?: ITaskConfig[];
    done: number;
    key: string;
    newYearType: QuestTypes;
    isNewYearTask: string;
    nextAvailableAt: number;
    reward: number | string;
    rewardCount: number;
    rewardDurationMs: number;
    rewardType: QuestRewardTypes;
    actionLink?: string;
    type: QuestConfigType;
    background?: string;
    title?: string;
    buttonText?: string;
    goal?: number;
    img?: string;
    modalTitle?: string;
    modalDescription?: string;
    modalButtonText?: string;
    modalHeaderBg?: string;
    questCompletePrompt?: string;
    modalHeaderImg?: string;
    logRender?: boolean;
    providerLink?: string;
    providerText?: string;
    clientPlatforms?: PlatformType[];
}

export interface IQuestsConfig {
    rewards: IRewardState[];
    tasks: ITaskConfig[];
}

export interface IExchangedSnakePairs {
    snake_from: number;
    snake_to: number;
}

export type IUserVariables = Partial<Record<StorageKeys, string>>;

export interface IPrizeByWheel {
    custom_params: {
        img: string;
        congrats: string;
        congratsSubtitle: string;
        id?: string;
        providerLink?: string;
    };
    lottery_reward_type: LotteryRewardTypes;
    success: true;
}

export interface RollRevolverParams {
    collectionId?: string;
    missionId?: string;
}

export enum ModalId {
    Draw = 'Raffle',
    Quests = 'Quests',
    Rewards = 'Rewards',
    Help = 'Help'
}

export enum ShortLinkType {
    TAB = 'tab',
    REWARD = 'reward',
    MISSION = 'mission'
}

export enum ShortLinkSubType {
    REWARDS = 'rewards',
    MISSIONS = 'missions',
    COLLECTIONS = 'collections',
    HELP = 'help',
    DRAW = 'draw'
}

export interface IShortLinkConfig {
    type: ShortLinkType;
    subtype?: ShortLinkSubType;
    id?: string;
}

export interface MainPageNavigation {
    id: string;
    label: string;
    iconSrc: string;
    modalId?: ModalId;
    ModalElem?: (props: ModalProps) => ReactElement;
    isGreenDotVisible?: () => boolean;
}

export interface IRawMissionReward {
    logKey?: string;
    activateLink: string;
    activateText: string;
    congrats: string;
    date: number;
    desc: string;
    img: string;
    modalBackground: string;
    modalDescription: string;
    modalTitle: string;
    modalImg: string;
    name: string;
    opt: string;
    providerLink: string;
    providerText: string;
    rewardAmount: string;
    rewardDurationMs: string;
    rewardType: string;
    text: string;
    missionId: string;
    isNewYear: boolean;
    background: string;
    lotteryRewardType: LotteryRewardTypes;
    collectionId?: number;
    logRender?: boolean;
    disableRoll?: boolean;
    notificationKey?: string;
}

export enum LotteryIds {
    snake_lottery_1 = 'snake_lottery_1',
    snake_lottery_2 = 'snake_lottery_2',
    snake_lottery_3 = 'snake_lottery_3',
    snake_lottery_4 = 'snake_lottery_4',
    snake_lottery_5 = 'snake_lottery_5',
    snake_lottery_6 = 'snake_lottery_6',
    snake_lottery_7 = 'snake_lottery_7',
    snake_lottery_8 = 'snake_lottery_8'
}

export const lotteryIdsArray = [
    LotteryIds.snake_lottery_1,
    LotteryIds.snake_lottery_2,
    LotteryIds.snake_lottery_3,
    LotteryIds.snake_lottery_4,
    LotteryIds.snake_lottery_5,
    LotteryIds.snake_lottery_6,
    LotteryIds.snake_lottery_7,
    LotteryIds.snake_lottery_8
];

export interface ILotteryConfig {
    availableTicket?: string;
}
